import Icon, { DeleteFilled, InfoCircleOutlined } from "@ant-design/icons";
import Tooltip from "antd/es/tooltip";
import { useGetForumByIdQuery } from "api/queries/forums";
import { DraggableTable } from "components/DraggableTable";
import { Button, Form, InputNumber, Space, Typography } from "components/styleguide";
import { toNumber } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toWallClockMoment } from "utils/dateUtils";
import { dateTimeSorter, numericalSorter } from "utils/sorterUtils";

import SessionCell from "../SessionCell";

import styles from "../styles.module.less";

/** @type {number[]} */
const emptyDuplicateSessionIds = [];

/** @type {Array<{ startTime?: string, endTime?: string }>} */
const emptyDuplicateTimePeriods = [];

const normalizeData = (data) => {
  return Array.isArray(data) ? data : [];
};

const getSessionKey = (session) => (Number(session?.id) > 0 ? `id-${session.id}` : `order-${session?.order}`);
const isNewSession = (session) => !Number(session?.id);

const getFormDuration = (form, session) => {
  const sessionFields = form.getFieldValue("sessions") ?? {};
  return sessionFields[getSessionKey(session)]?.duration;
};

const getSessionPeriod = (form, session) => {
  const duration = getFormDuration(form, session);
  const startTime = duration?.[0] ?? session?.startTime;
  const endTime = duration?.[1] ?? session?.endTime;

  if (!startTime || !endTime) {
    return null;
  }

  return { startTime, endTime };
};

const isSamePeriod = (firstPeriod, secondPeriod) => {
  if (!firstPeriod?.startTime || !firstPeriod?.endTime || !secondPeriod?.startTime || !secondPeriod?.endTime) {
    return false;
  }

  return (
    toWallClockMoment(firstPeriod.startTime).isSame(toWallClockMoment(secondPeriod.startTime), "minute") &&
    toWallClockMoment(firstPeriod.endTime).isSame(toWallClockMoment(secondPeriod.endTime), "minute")
  );
};

const SessionTable = ({
  data,
  form,
  loading,
  onAdd,
  onDelete,
  onInfo,
  onDragEnd,
  isSaveLoading,
  setDisableOrdering,
  disableOrdering = false,
  title,
  description,
  defaultSessionDuration,
  onDefaultSessionDurationChange,
  onSessionValuesChange,
  duplicateSessionIds = emptyDuplicateSessionIds,
  duplicateTimePeriods = emptyDuplicateTimePeriods,
  duplicateMessage,
  rowKey = "order",
}) => {
  const { t } = useTranslation();

  const dataSource = useMemo(() => normalizeData(data), [data]);
  const duplicateSessionIdSet = useMemo(() => new Set(duplicateSessionIds), [duplicateSessionIds]);

  const duplicatePeriods = useMemo(() => {
    const periods = duplicateTimePeriods.filter((period) => period?.startTime && period?.endTime);
    const duplicateSessionPeriods = dataSource
      .filter((session) => session?.id && duplicateSessionIdSet.has(session.id))
      .map((session) => getSessionPeriod(form, session))
      .filter(Boolean);

    return [...periods, ...duplicateSessionPeriods];
  }, [dataSource, duplicateSessionIdSet, duplicateTimePeriods, form]);

  const isLocked = (record) => {
    const dependencies = record?.dependencies;

    return !!(
      dependencies?.hasDependencies ||
      dependencies?.itineraryItemsCount > 0 ||
      dependencies?.seminarSessionsCount > 0
    );
  };

  const isDuplicate = (record) => {
    const recordPeriod = getSessionPeriod(form, record);
    const hasDuplicatePeriod = duplicatePeriods.some((period) => isSamePeriod(period, recordPeriod));

    if (hasDuplicatePeriod) {
      const periodSessions = dataSource.filter((session) =>
        isSamePeriod(getSessionPeriod(form, session), recordPeriod),
      );
      const hasNewSessionForPeriod = periodSessions.some(isNewSession);

      return hasNewSessionForPeriod ? isNewSession(record) : true;
    }

    return !!(record?.id && duplicateSessionIdSet.has(record.id));
  };

  const forumId = toNumber(useParams().id);
  const disabledDate = (value) => {
    const day = toWallClockMoment(value).startOf("day");
    const start = toWallClockMoment(forum.startDate).startOf("day");
    const end = toWallClockMoment(forum.endDate).endOf("day");

    return day.isBefore(start) || day.isAfter(end);
  };

  const yupSync = {
    async validator(_, duration) {
      if (!Array.isArray(duration) || !duration[0] || !duration[1]) {
        throw new Error(t("errors.required", { prop: "$t(Start and End Time)" }));
      }

      if (!moment(duration[0]).isValid() || !moment(duration[1]).isValid()) {
        throw new Error(t("errors.custom.sessionStartEndTimeInvalid"));
      }

      if (moment(duration[0]).isSame(moment(duration[1]))) {
        throw new Error(t("errors.custom.sessionEndTimeEqualStartTime"));
      }

      if (!moment(duration[0]).isBefore(moment(duration[1]))) {
        throw new Error(t("errors.custom.sessionEndTimeBeforeStartTime"));
      }
    },
  };

  const [forum, setForum] = useState({});

  useGetForumByIdQuery(
    { forumId },
    {
      onSuccess: (response) => {
        setForum(response.data);
      },
    },
  );

  useEffect(() => {
    const sessionValues = {};

    dataSource.forEach((record) => {
      sessionValues[getSessionKey(record)] = {
        duration: [
          record?.startTime ? toWallClockMoment(record.startTime) : null,
          record?.endTime ? toWallClockMoment(record.endTime) : null,
        ],
      };
    });

    form.resetFields(["sessions"]);
    form.setFieldsValue({ sessions: sessionValues });
  }, [dataSource, form, forum.startDate, forum.endDate]);

  const onChange = (_, __, sorter) => {
    setDisableOrdering(!!sorter?.order);
  };

  const columns = [
    {
      title: t("order"),
      dataIndex: "order",
      key: "order",
      sorter: (a, b) => numericalSorter(a.order, b.order),
    },
    {
      title: t("startTime"),
      dataIndex: "startTime",
      key: "startTime",
      sorter: (a, b) => dateTimeSorter(a.startTime, b.startTime),
    },
    {
      title: t("endTime"),
      dataIndex: "endTime",
      key: "endTime",
      sorter: (a, b) => dateTimeSorter(a.endTime, b.endTime),
    },
    {
      title: "",
      dataIndex: "id",
      key: "id",
      render: (_, record) => {
        if (isLocked(record)) {
          return (
            <div className={styles.buttonWrapper}>
              <Button title={t("View dependencies")} type="primary" onClick={() => onInfo(record)}>
                {t("Info")}
              </Button>
            </div>
          );
        }

        return (
          <div className={styles.buttonWrapper}>
            <Icon
              className={styles.deleteIcon}
              key={record.order}
              component={DeleteFilled}
              alt={t("delete")}
              onClick={() => onDelete(record)}
            />
          </div>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    return {
      ...col,
      onCell: (record) => {
        return {
          record,
          type: col.dataIndex === "startTime" || col.dataIndex === "endTime" ? "dateTime" : "text",
          index: col.dataIndex,
          title: col.title,
          editing: col.dataIndex === "startTime" || col.dataIndex === "endTime" ? 1 : 0,
          yupSync: yupSync,
          disabledDate,
          form,
          defaultSessionDuration,
          duplicate: isDuplicate(record),
          duplicateMessage,
          locked: isLocked(record),
          onSessionValuesChange,
        };
      },
    };
  });

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div className={styles.sessionsHeader}>
          <div>
            {title && (
              <Typography.Title style={{ marginBottom: 0 }} level={4}>
                {title}
              </Typography.Title>
            )}
            {description && <Typography.Text>{description}</Typography.Text>}
          </div>
          <Space align="center" style={{ marginTop: "auto" }}>
            <Typography.Text>{t("Default Session Duration")}:</Typography.Text>
            <Tooltip title={t("Used as the end time offset when adding a new session.")}>
              <InfoCircleOutlined className={styles.defaultDurationInfoIcon} />
            </Tooltip>
            <InputNumber
              min={0}
              precision={0}
              value={defaultSessionDuration}
              onChange={onDefaultSessionDurationChange}
              addonAfter={t("minutes")}
              style={{ width: 150 }}
            />
          </Space>
        </div>
        <Form form={form} component={false} onValuesChange={onSessionValuesChange}>
          <DraggableTable
            components={{
              body: {
                cell: SessionCell,
              },
            }}
            id="sessions-table"
            dataSource={dataSource}
            columns={mergedColumns}
            pagination={false}
            bordered={true}
            rowKey={rowKey}
            onDragEnd={onDragEnd}
            onChange={onChange}
            loading={loading}
            disableOrdering={disableOrdering}
            rowClassName={(record) => {
              if (isLocked(record)) {
                return styles.lockedSessionRow;
              }
              return "";
            }}
          ></DraggableTable>
        </Form>
        <Button onClick={onAdd} disabled={isSaveLoading}>
          {t("addSession")}
        </Button>
      </Space>
    </>
  );
};

SessionTable.propTypes = {
  editingKey: PropTypes.any,
  data: PropTypes.array,
  form: PropTypes.any,
  loading: PropTypes.bool,
  isEditing: PropTypes.func,
  onAdd: PropTypes.func,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onInfo: PropTypes.func,
  onDragEnd: PropTypes.func,
  isSaveLoading: PropTypes.bool,
  rowKey: PropTypes.string,
  setDisableOrdering: PropTypes.func,
  disableOrdering: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  defaultSessionDuration: PropTypes.number,
  onDefaultSessionDurationChange: PropTypes.func,
  onSessionValuesChange: PropTypes.func,
  duplicateSessionIds: PropTypes.array,
  duplicateTimePeriods: PropTypes.array,
  duplicateMessage: PropTypes.string,
};

SessionTable.defaultProps = {
  defaultSessionDuration: 30,
  onInfo: () => undefined,
  rowKey: "order",
};

export default SessionTable;
